/* Staff Fleet page → Contractors panel: add haulage contractors, invite their login, see linked trucks. */
import { useState } from "react";
import { HardHat, Plus, KeyRound, Unlink, Copy, Mail, Link2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", contact_name: "", email: "", phone: "", abn: "", notes: "", send_invite: true };

function InviteResult({ result, onClose }) {
  const copy = () => { navigator.clipboard?.writeText(result.temp_password || ""); toast.success("Copied"); };
  const inv = result.invite || {};
  return (
    <div className="space-y-3" data-testid="contractor-invite-result">
      <div className="text-sm text-ink">
        {result.linked_existing
          ? <>This contractor already had a portal login — your plant has now been <b>linked</b> to their account. Their existing password still works.</>
          : <>Login created for <b>{result.contractor.email}</b>.</>}
      </div>
      {result.temp_password && (
        <div className="border border-industrial p-3 flex items-center justify-between gap-2 bg-surface-2">
          <div><div className="label-tag">Temporary password</div><div className="mono text-lg font-black" data-testid="contractor-temp-password">{result.temp_password}</div></div>
          <button onClick={copy} className="text-primary-orange hover:underline mono text-xs inline-flex items-center gap-1" data-testid="contractor-copy-password"><Copy className="w-3 h-3" /> copy</button>
        </div>
      )}
      <div className={`text-xs mono inline-flex items-center gap-1 ${inv.status === "sent" ? "text-emerald-500" : inv.status === "failed" ? "text-red-400" : "text-muted-industrial"}`} data-testid="contractor-invite-status">
        <Mail className="w-3 h-3" /> Email: {inv.status || "skipped"}{inv.error ? ` — ${inv.error}` : ""}
      </div>
      <div className="text-[11px] text-muted-industrial">Portal URL: <span className="mono">{window.location.origin}/contractor/login</span></div>
      <DialogFooter><Button onClick={onClose} data-testid="contractor-invite-done" className="rounded-none bg-primary-orange uppercase tracking-widest text-xs font-bold">Done</Button></DialogFooter>
    </div>
  );
}

function ContractorCard({ c, onReset, onRevoke }) {
  return (
    <div className="card-v2 p-4" data-testid={`contractor-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="heading font-black text-base leading-tight truncate">{c.name}</div>
          <div className="text-xs text-muted-industrial truncate">{c.contact_name || "—"} · <span className="mono">{c.email}</span>{c.phone ? ` · ${c.phone}` : ""}</div>
        </div>
        {c.is_demo && <span className="mono text-[9px] uppercase border border-sky-500 text-sky-400 px-1.5 py-0.5">demo</span>}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div><div className="label-tag">Linked here</div><div className="mono font-bold" data-testid={`contractor-linked-count-${c.id}`}>{c.linked_trucks.length}</div></div>
        <div><div className="label-tag">Registered regos</div><div className="mono font-bold">{c.registrations.length}</div></div>
        <div><div className="label-tag">Last login</div><div className="mono">{c.user?.last_login_at ? new Date(c.user.last_login_at).toLocaleDateString() : "never"}</div></div>
      </div>
      {c.linked_trucks.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {c.linked_trucks.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 mono text-[10px] border border-industrial px-1.5 py-0.5" title={t.source === "manual" ? "Linked manually on the truck" : "Auto-matched by rego"} data-testid={`contractor-linked-truck-${c.id}-${t.number}`}>
              <Truck className="w-3 h-3 text-primary-orange" />{t.number}<span className="text-muted-industrial">· {t.source === "manual" ? "manual" : "rego"}</span>
            </span>
          ))}
        </div>
      )}
      {c.registrations.some((r) => !c.linked_trucks.find((t) => (t.rego || "").replace(/[^A-Z0-9]/gi, "").toUpperCase() === (r.rego || "").replace(/[^A-Z0-9]/gi, "").toUpperCase())) && (
        <div className="mt-2 text-[10px] mono text-muted-industrial">Unmatched regos: {c.registrations.filter((r) => !c.linked_trucks.find((t) => (t.rego || "").replace(/[^A-Z0-9]/gi, "").toUpperCase() === (r.rego || "").replace(/[^A-Z0-9]/gi, "").toUpperCase())).map((r) => r.rego).join(", ")} — add the rego to a truck or link it manually.</div>
      )}
      <div className="mt-3 pt-3 border-t border-industrial flex items-center justify-between">
        <button onClick={() => onReset(c)} data-testid={`contractor-reset-pw-${c.id}`} className="mono text-[10px] uppercase text-primary-orange hover:underline inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset password</button>
        <button onClick={() => onRevoke(c)} data-testid={`contractor-revoke-${c.id}`} className="mono text-[10px] uppercase text-muted-industrial hover:text-red-400 inline-flex items-center gap-1"><Unlink className="w-3 h-3" /> Revoke access</button>
      </div>
    </div>
  );
}

export default function ContractorsPanel({ contractors, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Company name and email are required");
    setBusy(true);
    try {
      const r = await api.post("/contractors", form);
      setResult(r.data); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const reset = async (c) => {
    if (!window.confirm(`Issue a new temporary password for ${c.name}? Their current password stops working.`)) return;
    try {
      const r = await api.post(`/contractors/${c.id}/reset-password`);
      setResult({ contractor: c, temp_password: r.data.temp_password, invite: r.data.invite, linked_existing: false }); setOpen(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revoke = async (c) => {
    if (!window.confirm(`Revoke ${c.name}'s access to this plant? They will no longer see any of your trucks.`)) return;
    try { await api.delete(`/contractors/${c.id}/access`); toast.success("Access revoked"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const close = () => { setOpen(false); setResult(null); setForm(EMPTY); };
  const field = (k, label, props = {}) => (
    <div><Label className="label-tag">{label}</Label><Input data-testid={`contractor-form-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-none bg-transparent border-industrial mono" {...props} /></div>
  );

  return (
    <section className="space-y-4" data-testid="contractors-panel">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag flex items-center gap-1"><HardHat className="w-3 h-3 text-primary-orange" /> CONTRACTOR PORTAL</div>
          <h2 className="heading text-xl font-black uppercase">Haulage <span className="text-primary-orange">Contractors</span></h2>
          <p className="text-xs text-muted-industrial mt-1 max-w-2xl">Owner-drivers and haulage companies that run trucks for you but don't own a plant. They get a private map of <b>their</b> trucks only — plant, job site and live ETA. Link a truck by matching rego or via the <Link2 className="inline w-3 h-3" /> Contractor field on the truck.</p>
        </div>
        <Button data-testid="add-contractor-btn" onClick={() => { setResult(null); setForm(EMPTY); setOpen(true); }} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold"><Plus className="w-4 h-4 mr-1" />Add Contractor</Button>
      </div>
      {contractors.length === 0 && <div className="card-v2 p-6 text-sm text-muted-industrial text-center" data-testid="contractors-empty">No contractors linked to this plant yet.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {contractors.map((c) => <ContractorCard key={c.id} c={c} onReset={reset} onRevoke={revoke} />)}
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
        <DialogContent className="rounded-none bg-surface border-industrial max-w-lg" data-testid="contractor-dialog">
          <DialogHeader><DialogTitle className="heading uppercase">{result ? "Contractor login" : "Add contractor"}</DialogTitle></DialogHeader>
          {result ? <InviteResult result={result} onClose={close} /> : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">{field("name", "Company name", { placeholder: "Derwent Haulage Pty Ltd" })}</div>
                {field("contact_name", "Contact person")}
                {field("email", "Login email", { type: "email", placeholder: "ops@haulage.com.au" })}
                {field("phone", "Phone")}
                {field("abn", "ABN")}
                <div className="col-span-2">{field("notes", "Notes")}</div>
                <label className="col-span-2 flex items-center gap-2 text-xs text-ink cursor-pointer">
                  <input type="checkbox" data-testid="contractor-form-send-invite" checked={form.send_invite} onChange={(e) => setForm({ ...form, send_invite: e.target.checked })} className="w-3.5 h-3.5" /> Email the login details to the contractor
                </label>
              </div>
              <div className="text-[10px] mono uppercase text-muted-industrial">If this email already has a contractor login (from another plant), your plant is simply linked to it — no new password is issued.</div>
              <DialogFooter><Button data-testid="contractor-form-submit" onClick={create} disabled={busy} className="rounded-none bg-primary-orange hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-xs font-bold">{busy ? "Creating…" : "Create & Invite"}</Button></DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
